/**
 * Textos de `plan` en español — la fuente. `en/plan.ts` tiene que tener
 * exactamente las mismas claves (lo exige el tipo), así que una clave nueva
 * acá rompe el typecheck hasta que alguien la traduce.
 *
 * Interpolación con funciones, no con plantillas "{n}": el compilador revisa
 * los argumentos y cada idioma arma su propia gramática (plurales, orden).
 *
 * El precio ("USD 30") y los códigos de plan/cupón NO viven acá: no se
 * traducen. Llegan como argumento.
 */
const plan = {
  /** `lib/plan.ts`: el nombre y la cadencia del plan. */
  labels: {
    /** Lo que sigue al precio: "USD 30 al mes". */
    cadence: 'al mes',
    /** El plan mensual (el único que se vende hoy) delante del candidato. */
    monthly: (price: string) => `Plan TBit Jobs — ${price} al mes`,
    /** El trimestral de antes: sigue vivo en cuentas que ya lo compraron. Sin precio: ya no se vende. */
    quarterly: 'Plan TBit Jobs trimestral',
    /** Planes viejos de pago único: el código crudo va tal cual (PRO, STARTER…). */
    legacy: (code: string) => `Plan ${code}`,
    none: 'Sin plan activo',
  },
  /** La etiqueta de marca sobre el titular, en la oferta y en la tab. */
  yourPlan: 'Tu plan',
  /** `PlanOffer`: la oferta, la misma en los tres sitios. */
  offer: {
    benefits: {
      send: 'Postulamos por ti todos los días hábiles, sin que abras la computadora.',
      mail: 'Las respuestas de los empleadores llegan a tu correo de siempre.',
      doc: 'Adaptamos tu hoja de vida y tu mensaje a cada oferta.',
    },
    /**
     * Debajo del precio rebajado, cuando el descuento es el del socio que lo
     * trajo. `months` es por cuántos meses pagados rige (el del socio: 1).
     */
    partner: (code: string, amount: string, months: number | 'forever') =>
      months === 'forever'
        ? `Con el código ${code}: ${amount} menos todos los meses.`
        : months === 1
          ? `Con el código ${code}: ${amount} menos en tu primer mes.`
          : `Con el código ${code}: ${amount} menos en cada uno de tus primeros ${months} meses.`,
    /**
     * Junto al precio rebajado, en vez de «al mes», cuando el descuento no es
     * para siempre: «USD 23 el primer mes».
     */
    firstMonths: (months: number) => (months === 1 ? 'el primer mes' : `al mes, los primeros ${months} meses`),
    /** Debajo del precio rebajado temporal: lo que se paga cuando se acaba. */
    after: (price: string) => `Después, ${price} al mes.`,
    /** aria-label del precio de lista tachado. */
    listPrice: (price: string) => `Precio normal ${price}`,
    secureNote: 'Pago seguro con Stripe. Se renueva cada mes.',
    /**
     * La casilla OBLIGATORIA de los términos de TBit Jobs (precio, prueba,
     * cancelación, reembolsos), sobre el botón. Sin marcarla no hay checkout:
     * el servidor la exige sobre la versión vigente.
     */
    terms: {
      before: 'Acepto los ',
      label: 'Términos y condiciones de TBit Jobs',
      href: 'https://tbit.app/es/legal/tbit-jobs-terminos-y-condiciones',
      after: '.',
      /** Clic en el botón sin marcar la casilla. */
      required: 'Marca la casilla para aceptar los términos y continuar.',
      /** El servidor ya va por otra versión: hay que leerla y volver a marcar. */
      outdated: 'Actualizamos los términos. Léelos y vuelve a marcar la casilla.',
    },
    /**
     * La prueba de 7 días, sólo para quien nunca tuvo plan. Las condiciones van
     * enteras y a la vista, debajo del botón: se pide la tarjeta, cuándo se cobra,
     * cuánto, y que cancelar antes no cuesta nada.
     */
    trial: {
      /** Sobre el precio. */
      badge: 'Los primeros 7 días, gratis',
      card: 'Te pedimos la tarjeta para empezar, pero no te cobramos nada durante 7 días.',
      charge: (price: string) => `El cobro llega el día 8: ${price} al mes desde ahí.`,
      /** Con un descuento que dura `months` meses pagados (el del socio: 1). */
      chargeIntro: (first: string, months: number, price: string) =>
        months === 1
          ? `El cobro llega el día 8: ${first} ese primer mes y ${price} al mes desde ahí.`
          : `El cobro llega el día 8: ${first} al mes los primeros ${months} meses y ${price} al mes desde ahí.`,
      cancel: 'Si cancelas antes de que terminen los 7 días, no pagas nada.',
      secureNote: 'Pago seguro con Stripe.',
    },
    errors: {
      /** 409 `already_subscribed`: Stripe dice que la suscripción sigue viva. */
      alreadySubscribed: 'Tu suscripción sigue activa. Recarga la página.',
      checkoutUnavailable: 'No pudimos abrir el pago en este momento. Escríbenos y activamos tu plan contigo.',
      checkoutFailed: 'No pudimos abrir el pago. Inténtalo de nuevo en un momento.',
      network: 'Revisa tu conexión e inténtalo de nuevo.',
    },
  },
  /** El código de descuento, dentro de la oferta y en `lib/coupon.ts`. */
  coupon: {
    open: '¿Tienes un código de descuento?',
    label: 'Código de descuento',
    placeholder: 'TU CÓDIGO',
    apply: 'Aplicar',
    remove: 'Quitar',
    /**
     * Debajo del campo, ANTES de «Aplicar»: validar un código de socio ya lo
     * deja como tu referidor, así que se avisa antes del clic.
     */
    partnerHint: 'Si es el código de un socio, compartimos tu correo y el estado de tu plan con quien te invitó.',
    expired: 'Ese código no existe o ya venció.',
    unavailable: 'No pudimos validar el código; puedes intentarlo en la página de pago.',
    /** «Código DANIELA aplicado» — el código va tal cual. */
    applied: (code: string) => `Código ${code} aplicado`,
    once: 'en tu primer mes de pago',
    /** `repeating` por 1 mes: el del socio y el de LANZAMIENTO. */
    firstMonth: 'en tu primer mes',
    forever: 'todos los meses',
    months: (n: number) => `durante ${n} ${n === 1 ? 'mes' : 'meses'}`,
  },
  /**
   * `PlanNudge`: la cinta de todas las pantallas y el aviso al abrir la app,
   * mientras no haya plan activo. Tres casos: nunca activó la prueba, tuvo
   * plan y se le venció, o probó y no siguió.
   */
  nudge: {
    trial: {
      ribbon: 'Tu prueba gratis no está activa: todavía no estamos postulando por ti.',
      headline: 'Tu prueba gratis no está activa',
      lede: 'Mientras no la actives, tus postulaciones no salen a ningún empleador. Son 7 días gratis y puedes cancelar cuando quieras.',
    },
    reactivate: {
      ribbon: 'Tu plan venció: no estamos postulando por ti.',
      headline: 'Tu plan no está activo',
      lede: 'Tus postulaciones no salen a empleadores hasta que lo reactives. Seguimos desde donde quedaste.',
    },
    activate: {
      ribbon: 'No tienes un plan activo: no estamos postulando por ti.',
      headline: 'No tienes un plan activo',
      lede: 'Tus postulaciones no salen a empleadores hasta que actives tu plan.',
    },
  },
  /** El texto de TODO botón que lleva al plan: lo elige `usePlanCta` según `cta_case` del servidor. */
  cta: {
    trial: {
      label: 'Empieza tus 7 días gratis',
      note: 'Te pedimos la tarjeta, pero no te cobramos nada durante 7 días.',
    },
    reactivate: {
      label: 'Reactivar mi plan',
      note: (price: string) => `${price} al mes. Seguimos desde donde quedaste.`,
    },
    activate: {
      label: 'Activar mi plan',
      note: (price: string) => `${price} al mes. Cancelas cuando quieras.`,
    },
  },
  /** `PaywallModal`: el muro, en modal. */
  paywall: {
    /** aria-label del diálogo. */
    label: 'Activa tu plan',
    headline: 'Ya viste cómo llegan tus postulaciones',
    lede: 'Activa tu plan y las enviamos a los empleadores de verdad, todos los días hábiles.',
    /**
     * La oferta ANTES de la tercera muestra, debajo de las postulaciones. Hasta
     * 2026-10-10 no existía: el plan sólo aparecía al mandarse las tres, y quien
     * se quedaba en dos nunca veía que había una prueba gratis.
     */
    early: {
      headline: 'Cuando quieras, postulamos por ti',
      lede: 'Tus primeros 7 días son gratis. Mándate las muestras de arriba para ver cómo llegan, o empieza ya: salen a los empleadores todos los días hábiles.',
      ledeNoTrial: 'Mándate las muestras de arriba para ver cómo llegan, o activa tu plan ya: salen a los empleadores todos los días hábiles.',
    },
    notNow: 'Ahora no',
    /** aria-label del fondo. */
    close: 'Cerrar',
  },
  /** `JobApplyPlanPage`: la tab Plan del perfil. */
  /**
   * `PaymentPausedNotice`: el cobro falló (past_due). El plan sigue siendo suyo,
   * pero no sale ninguna postulación hasta que pague. El botón va al enlace
   * firmado del servidor (`pay_url`): abre el cambio de tarjeta y cobra al volver.
   */
  paused: {
    title: 'Tu pago no pasó',
    body: 'Pausamos tus postulaciones hasta que actualices tu método de pago. Apenas lo cambies, hacemos el cobro y volvemos a postular por ti.',
    cta: 'Actualizar método de pago',
  },
  page: {
    loading: 'Cargando tu plan',
    loadError: 'No pudimos leer tu plan. Recarga la página.',
    active: 'Activo',
    expired: 'Vencido',
    /** «Se renueva el <b>fecha</b> por USD 30 al mes.» */
    renewsBefore: 'Se renueva el ',
    renewsAfter: (price: string) => ` por ${price}.`,
    /** Planes viejos (trimestral, pago único): la fecha sin precio, que ya no es el de hoy. */
    renewsPlain: '.',
    /** En la prueba gratis: «Prueba gratis hasta el <b>fecha</b>. Al terminar…» */
    trialing: 'Prueba gratis',
    trialBefore: 'Prueba gratis hasta el ',
    trialAfter: (price: string) =>
      `. Al terminar, se cobran ${price} al mes. Si cancelas antes, no pagas nada.`,
    activeBody:
      'Mientras esté activo postulamos por ti todos los días hábiles, adaptamos tu hoja de vida a cada oferta y te llevamos las respuestas a tu correo.',
    /** Sin cuenta de cobro en Stripe (cortesía, pago viejo): el cambio se hace con nosotros. */
    changeOrCancel: '¿Necesitas cambiar o cancelar tu plan? Escríbenos y lo hacemos contigo.',
    /** Con cuenta de cobro: el portal de clientes de Stripe, que vuelve a esta tab. */
    manage: {
      cta: 'Administrar o cancelar mi plan',
      opening: 'Abriendo...',
      note: 'Cambia tu tarjeta, mira tus facturas o cancela cuando quieras.',
      noBillingAccount: 'Tu plan no se administra desde aquí. Escríbenos y lo cambiamos o cancelamos contigo.',
      failed: 'No pudimos abrir la administración de tu plan. Inténtalo de nuevo en un momento.',
      /** Plan vencido con cuenta de cobro: casi siempre es un cobro fallido. */
      failedCharge: '¿Falló el cobro? Actualiza tu tarjeta aquí.',
    },
    /** «<b>Plan …</b> venció el 14 de agosto de 2026. Mientras tanto …» */
    endedOn: (date: string) => ` venció el ${date}.`,
    notCurrent: ' ya no está vigente.',
    meanwhile: ' Mientras tanto no salen postulaciones a empleadores.',
    reactivate: {
      headline: 'Reactiva tu plan',
      lede: 'Volvemos a postular por ti todos los días hábiles, desde donde quedaste.',
    },
    discover: {
      headline: 'Postulamos por ti, todos los días',
      lede: 'Tú ya viste cómo llega tu postulación. Con el plan activo, esas mismas salen a los empleadores mientras tú haces otra cosa.',
    },
    compare: {
      headline: 'Tú postulas. Con el plan, postulamos por ti.',
      have: {
        title: 'TBit Jobs',
        chip: 'Ya lo tienes',
        items: [
          'Las vacantes que van con tu perfil',
          'Postular por tu cuenta con una carta en inglés',
          'El puntaje de tu hoja de vida y lo que le falta',
          'Tu perfil público y la bolsa de talento',
        ],
        footer: 'Siempre, sin tarjeta.',
      },
      planTitle: 'Plan TBit Jobs',
      ribbon: 'Lo hacemos por ti',
      photoAlt: 'Camarera de hotel junto a su carro de toallas',
      caption: 'Mientras trabajas, TBit postula.',
      points: [
        'Postulamos por ti todos los días hábiles.',
        'Una carta para cada empleador, escrita con tu hoja de vida.',
        'Leemos las respuestas, te decimos cuáles son una oportunidad y qué contestar.',
        'Reescribimos tu hoja de vida y la traducimos para cada país.',
      ],
      never:
        'El plan no compra un puesto, prioridad ante un empleador ni vacantes que no veas gratis. Todas las vacantes están en el buscador, con o sin plan: el plan las elige por ti y postula. Apareces igual en las búsquedas.',
    },
  },
};

export default plan;
